/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {UserService} from '../../../shared/services/user.service';
import {Permission} from '../../../shared/utils/permission.enum';
import {forkJoin, ReplaySubject, Subscription} from 'rxjs';
import {ClaimsApi} from '../../../api/ms-claims/claims.api';
import {SortDirection} from '../../../api/generic/contracts/sort-direction.enum';
import {ClaimListFilterColumnEnum} from './claim-list-filter-column.enum';
import {ClaimListItemModel} from './claim-list-item.model';
import {ClaimResultModel} from '../../../api/ms-claims/contracts/claim-result.model';
import {ClaimSummaryModel} from '../../../api/ms-claims/contracts/claim-summary.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ConfirmModalComponent} from '../../../shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {ConfirmModal} from '../../../shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {AlertNotificationSeverity} from '../../../alert-notification-severity.model';
import {PartnerBaseModel} from '../../../api/ms-claims/contracts/partner-base.model';
import {CodeFilterModel} from '../../../shared/filter/code-filter/code-filter.model';
import {PartnerGroupBaseModel} from '../../../api/ms-claims/contracts/partner-group-base.model';
import {ReferenceDataApi} from '../../../api/ms-claims/reference-data.api';
import {AvailableActions} from '../../../api/ms-claims/contracts/available-actions.enum';
import {ClaimSendHelperService} from '../../services/claim-send-helper.service';
import {Router} from '@angular/router';
import {PrettyPrintReferencePipe} from '../../../shared/pipes/pretty-print-reference/pretty-print-reference.pipe';
import {ClaimContextMenuModel} from './claim-context-menu.model';
import {ToastWrapperService} from '../../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {FolderDeleteService} from '../../services/folder-delete.service';
import {SupplyingPackingPlantBaseModel} from '../../../api/ms-claims/contracts/supplying-packing-plant-base.model';
import {ClaimSearchCriteriaService} from '../../services/claim-search-criteria.service';
import {ClaimQueryModel} from '../../../api/ms-claims/contracts/claim-query.model';
import {CodeFilterComponent} from '../../../shared/filter/code-filter/code-filter.component';
import {
    InputAndCodeFilterComponent
} from '../../../shared/filter/input-and-code-filter/input-and-code-filter.component';
import {DateFilterComponent} from '../../../shared/filter/date-filter/date-filter.component';
import {
    InputAndCodeFilterStateModel
} from '../../../shared/filter/input-and-code-filter/input-and-code-filter-state.model';
import {AbstractFilterStateModel} from '../../../shared/filter/abstract-filter-state.model';
import {CodeFilterStateModel} from '../../../shared/filter/code-filter/code-filter-state.model';
import {DateFilterStateModel} from '../../../shared/filter/date-filter/date-filter-state.model';
import {SpinnerService} from '../../../shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../../shared/spinner/spinner-context-type.enum';

@Component({
    selector: 'app-claim-list',
    templateUrl: './claim-list.component.html',
    styleUrls: ['./claim-list.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClaimListComponent implements OnInit, OnDestroy, AfterViewInit {

    /** Filter template variables **/
    @ViewChild('partnerFilterComponent') partnerFilterComponent: CodeFilterComponent;
    @ViewChild('supplyingPackingPlantFilterComponent') supplyingPackingPlantFilterComponent: CodeFilterComponent;
    @ViewChild('numberFilterComponent') numberFilterComponent: InputAndCodeFilterComponent;
    @ViewChild('referenceNumberFilterComponent') referenceNumberFilterComponent: InputAndCodeFilterComponent;
    @ViewChild('statusFilterComponent') statusFilterComponent: CodeFilterComponent;
    @ViewChild('sentDateFilterComponent') sentDateFilterComponent: DateFilterComponent;
    @ViewChild('materialFilterComponent') materialFilterComponent: InputAndCodeFilterComponent;
    @ViewChild('modelCodeFilterComponent') modelCodeFilterComponent: InputAndCodeFilterComponent;
    @ViewChild('modelTypeFilterComponent') modelTypeFilterComponent: InputAndCodeFilterComponent;
    @ViewChild('lotNumberFilterComponent') lotNumberFilterComponent: InputAndCodeFilterComponent;
    @ViewChild('claimCodeFilterComponent') claimCodeFilterComponent: CodeFilterComponent;
    @ViewChild('changedOnFilterComponent') changedOnFilterComponent: DateFilterComponent;

    @Output() reportCriteria = new EventEmitter<ClaimQueryModel>();

    public claimItems: ClaimListItemModel[];
    public showClearAllFilters: boolean;

    /** Loading variables **/
    public isLoading = false;
    public tableIsLoading: boolean;
    /** Enum variables **/
    Permission = Permission;
    ClaimListFilterColumnEnum = ClaimListFilterColumnEnum;
    /** Filter options variables **/
    public partnerOptions = new ReplaySubject<CodeFilterModel[]>(1);
    public supplyingPackingPlantOptions = new ReplaySubject<CodeFilterModel[]>(1);
    public statusOptions = new ReplaySubject<CodeFilterModel[]>(1);
    public claimCodeOptions = new ReplaySubject<CodeFilterModel[]>(1);
    public partnerArrowUp = false;
    public supplyingPackingPlantArrowUp = false;
    public changedOnArrowUp = false;
    public lotNumberArrowUp = false;
    public numberArrowUp = false;
    public materialArrowUp = false;
    public statusArrowUp = false;
    public sentDateArrowUp = false;
    public referenceNumberArrowUp = false;
    public modelTypeArrowUp = false;
    public modelCodeArrowUp = false;
    public claimCodeArrowUp = false;
    /** Subscription variable **/
    private subscriptions: Subscription = new Subscription();

    constructor(private spinnerService: SpinnerService,
                private userService: UserService,
                private modalService: NgbModal,
                private toastrService: ToastWrapperService,
                private claimsApi: ClaimsApi,
                private referenceDataApi: ReferenceDataApi,
                private claimSendHelper: ClaimSendHelperService,
                private router: Router,
                private prettyPrintReferencePipe: PrettyPrintReferencePipe,
                private folderDeleteService: FolderDeleteService,
                public claimSearchCriteriaService: ClaimSearchCriteriaService) {
        this.subscriptions.add(this.spinnerService
                .onGlobalChange()
                .subscribe((isLoading) => {
                    this.isLoading = isLoading;
                }));

        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.constructor);
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, null);
    }

    ngOnInit(): void {
        this.getClaims();

        /** Subscription to handle table loading **/
        this.subscriptions.add(
                this.claimSendHelper.enableLoading.subscribe(enableLoading => {
                    this.tableIsLoading = enableLoading;
                })
        );
    }

    ngAfterViewInit(): void {
        this.restoreFilters();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Get the Claims from the BE with filter and ordering criteria
     */
    getClaims = (hasPaginationCriteria?: boolean): void => {
        // Start table loading
        this.tableIsLoading = true;
        const claimQuery = this.claimSearchCriteriaService.getClaimQuery(hasPaginationCriteria);
        this.subscriptions.add(
                this.claimsApi.getClaims(claimQuery)
                        .subscribe(claimResult => {
                            this.mapItems(claimResult);
                        }).add(() => {
                    this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.constructor);
                    this.tableIsLoading = false;
                })
        );
        this.emitReportCriteria(claimQuery);
    };

    restoreFilters(): void {
        this.partnerFilterComponent?.initFilter(
                this.claimSearchCriteriaService.stateMap
                        .get(ClaimListFilterColumnEnum.PARTNER) as CodeFilterStateModel);
        this.supplyingPackingPlantFilterComponent?.initFilter(
                this.claimSearchCriteriaService.stateMap
                        .get(ClaimListFilterColumnEnum.SUPPLYING_PACKING_PLANT) as CodeFilterStateModel);
        this.numberFilterComponent?.initFilter(
                this.claimSearchCriteriaService.stateMap
                        .get(ClaimListFilterColumnEnum.NUMBER) as InputAndCodeFilterStateModel);
        this.referenceNumberFilterComponent?.initFilter(
                this.claimSearchCriteriaService.stateMap
                        .get(ClaimListFilterColumnEnum.REFERENCE_NUMBER) as InputAndCodeFilterStateModel);
        this.statusFilterComponent?.initFilter(
                this.claimSearchCriteriaService.stateMap
                        .get(ClaimListFilterColumnEnum.STATUS) as CodeFilterStateModel);
        this.sentDateFilterComponent?.initFilter(
                this.claimSearchCriteriaService.stateMap
                        .get(ClaimListFilterColumnEnum.SENT_DATE) as DateFilterStateModel);
        this.materialFilterComponent?.initFilter(
                this.claimSearchCriteriaService.stateMap
                        .get(ClaimListFilterColumnEnum.MATERIAL) as InputAndCodeFilterStateModel);
        this.modelCodeFilterComponent?.initFilter(
                this.claimSearchCriteriaService.stateMap
                        .get(ClaimListFilterColumnEnum.MODEL_CODE) as InputAndCodeFilterStateModel);
        this.modelTypeFilterComponent?.initFilter(
                this.claimSearchCriteriaService.stateMap
                        .get(ClaimListFilterColumnEnum.MODEL_TYPE) as InputAndCodeFilterStateModel);
        this.lotNumberFilterComponent?.initFilter(
                this.claimSearchCriteriaService.stateMap
                        .get(ClaimListFilterColumnEnum.LOT_NUMBER) as InputAndCodeFilterStateModel);
        this.changedOnFilterComponent?.initFilter(
                this.claimSearchCriteriaService.stateMap
                        .get(ClaimListFilterColumnEnum.LAST_CHANGE_DATETIME) as DateFilterStateModel);
        this.claimCodeFilterComponent?.initFilter(
                this.claimSearchCriteriaService.stateMap
                        .get(ClaimListFilterColumnEnum.CLAIM_CODE) as CodeFilterStateModel);

        this.setShowClearAllFilters();
        this.arrowToggle();
    }

    getPartners = (): void => {
        const subscription = this.referenceDataApi.getPartnersGroupOptions().subscribe((data: PartnerGroupBaseModel[]) => {
            this.partnerOptions.next(data.map((partnerGroup: PartnerGroupBaseModel) => {
                const codeFilter = {
                    name: partnerGroup.country.name,
                    code: partnerGroup.country.alpha2Code,
                    active: partnerGroup.country.active,
                    checked: true,
                    indeterminate: false,
                    children: []
                } as CodeFilterModel;

                partnerGroup.partners.forEach((partner: PartnerBaseModel) => {
                    codeFilter.children.push({
                        name: partner.name,
                        code: partner.code,
                        active: partner.active,
                        checked: true,
                        indeterminate: false,
                        father: codeFilter
                    } as CodeFilterModel);
                });
                return codeFilter;
            }));
        });
        this.subscriptions.add(subscription);
    };

    getSupplyingPackingPlants = (): void => {
        const subscription = this.referenceDataApi.getSupplyingPackingPlantsGroupOptions()
                .subscribe((supplyingPackingPlants: SupplyingPackingPlantBaseModel[]) => {
                    this.supplyingPackingPlantOptions.next(supplyingPackingPlants.map(supplyingPackingPlant => ({
                        code: supplyingPackingPlant.code,
                        name: supplyingPackingPlant.name,
                        active: supplyingPackingPlant.active,
                        checked: true,
                        tooltipText: this.prettyPrintReferencePipe.transform(supplyingPackingPlant)
                    } as CodeFilterModel)));
                });
        this.subscriptions.add(subscription);
    };

    getStatus = (): void => {
        this.subscriptions.add(
                this.referenceDataApi.getStatusOptions().subscribe(options => {
                    this.statusOptions.next(options.map(option => ({
                        name: option.description,
                        code: option.code,
                        active: option.active,
                        checked: true,
                        indeterminate: false,
                        tooltipText: option.description === option.longDescription ? null : option.longDescription
                    } as CodeFilterModel)));
                }));
    };

    getClaimCodes = (): void => {
        this.subscriptions.add(
                this.referenceDataApi.getClaimCodes().subscribe(options => {
                    this.claimCodeOptions.next(options.map(option => ({
                        name: option.name,
                        code: option.code,
                        active: option.active,
                        checked: true,
                        indeterminate: false,
                        tooltipText: this.prettyPrintReferencePipe.transform(option)
                    } as CodeFilterModel)));
                }));
    };

    /**
     * Changes the Claim list page and gets the corresponding Claims
     * @param pageNumber The page that the list will change to
     */
    changePage = (pageNumber: number): void => {
        this.claimSearchCriteriaService.pageNumber = pageNumber;
        this.getClaims(true);
    };

    /**
     * Maps ClaimResultModel to ClaimListItemModel and sets it to {@link claimItems}
     * Sets the {@link totalResults} value
     * @param claimSearchResult Claim list to be mapped
     */
    mapItems = (claimSearchResult: ClaimResultModel): void => {
        this.claimSearchCriteriaService.totalResults = claimSearchResult.totalResults;

        if (claimSearchResult.claimsPage != null) {
            this.claimItems = claimSearchResult.claimsPage.map(claim => ({
                claim: claim,
                positions: [],
                isOpen: false,
                isTouched: false,
                contextMenuOptions: this.mapAvailableActions(claim.availableActions)
            } as ClaimListItemModel));
        } else {
            this.claimItems = [];
        }
    };

    mapAvailableActions(availableActions: AvailableActions[]): ClaimContextMenuModel {
        let canSubmit = false;
        let canEdit = false;
        let canDelete = false;
        let canCreate = false;
        if (availableActions != null) {
            canSubmit = availableActions.includes(AvailableActions.SUBMIT);
            canEdit = availableActions.includes(AvailableActions.EDIT);
            canDelete = availableActions.includes(AvailableActions.DELETE);
            canCreate = availableActions.includes(AvailableActions.CREATE);
        }
        return {
            canSubmit: canSubmit,
            canEdit: canEdit,
            canDelete: canDelete,
            canCreate: canCreate
        };
    }

    hasPartnerColumn = (): boolean => this.userService.isMultiPartner()
            || this.userService.hasPermissions([Permission.ADMIN_SPO_VIEW]);

    /**
     * Set material number filter *
     * {@link changePage} keeps the selected page number
     * @param state The emitted code filter state
     */
    onFilterChange(state: AbstractFilterStateModel): void {
        this.claimSearchCriteriaService.setState(state.filterName as ClaimListFilterColumnEnum, state, () => {
            this.getClaims();
            this.setShowClearAllFilters();
        });
    }

    /**
     * Deletes a Claim
     * @param claimSummary The Claim summary to be deleted
     */
    deleteClaim(claimSummary: ClaimSummaryModel): void {
        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent, {
            centered: true
        }).componentInstance;

        const confirmModal: ConfirmModal = {
            titleText: 'Delete',
            subtitleText: `Are you sure you want to delete draft Claim number ${claimSummary.number}?`,
            alertNotificationSeverity: AlertNotificationSeverity.ERROR,
            primaryButtonText: 'Delete',
            primaryButtonCustomizedClass: 'btn-primary-red',
            secondaryButtonText: 'Cancel',
            manualChecks: {
                title: `<strong>Warning:</strong> This action cannot be undone`,
                inputs: [
                    {
                        checked: false,
                        message: 'All header, position information and files attached will be deleted'
                    }
                ]
            }
        };

        confirmModalInstance.initModal(confirmModal).confirmEvent.subscribe(() => {
            this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.constructor);
            forkJoin([
                this.claimsApi.deleteClaim(claimSummary.number),
                this.folderDeleteService.deleteFolder(claimSummary.attachmentDirectory)
            ]).subscribe(() => {
                this.changePage(this.claimSearchCriteriaService.pageNumber);
                this.toastrService.success(`${claimSummary.number} <strong>deleted</strong> successfully`, 'Success');
            }, error => {
                this.toastrService.error(`An error has occurred. ${error}`, 'Error');
            }).add(() => {
                this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.constructor);
            });
        });
    }

    /**
     * Send an Claim from the context menu
     * {@link changePage} keeps the selected page number
     * @param claim The claim summary model
     */
    contextMenuSend(claim: ClaimSummaryModel): void {
        this.subscriptions.add(
                this.claimSendHelper.sendClaim(claim.number, claim.material.supplyingPackingPlantCode, claim.material.number, () => {
                    this.changePage(this.claimSearchCriteriaService.pageNumber);
                }));
    }

    contextMenuEdit(claimNumber: string): void {
        this.router.navigate(['/', 'claim', claimNumber, 'edit']);
    }

    /** Clears all filters **/
    clearAllFilters(): void {
        this.claimSearchCriteriaService.clear();
        this.setAllFiltersInactive();
        this.getClaims();
        this.setShowClearAllFilters();
    }

    /** Sets all filters as inactive **/
    setAllFiltersInactive(): void {
        if (this.hasPartnerColumn()) {
            this.partnerFilterComponent.resetFilter();
        }
        this.supplyingPackingPlantFilterComponent.resetFilter();
        this.lotNumberFilterComponent.resetFilter();
        this.numberFilterComponent.resetFilter();
        this.materialFilterComponent.resetFilter();
        this.statusFilterComponent.resetFilter();
        this.sentDateFilterComponent.resetFilter();
        this.changedOnFilterComponent.resetFilter();
        this.referenceNumberFilterComponent.resetFilter();
        this.modelTypeFilterComponent.resetFilter();
        this.modelCodeFilterComponent.resetFilter();
        this.claimCodeFilterComponent.resetFilter();
    }

    /** Sets the visibility of the clear all filters button **/
    setShowClearAllFilters(): void {
        this.showClearAllFilters = (this.hasPartnerColumn() && this.partnerFilterComponent?.isFilterActive)
                || this.supplyingPackingPlantFilterComponent?.isFilterActive
                || this.lotNumberFilterComponent?.isFilterActive
                || this.numberFilterComponent?.isFilterActive
                || this.materialFilterComponent?.isFilterActive
                || this.statusFilterComponent?.isFilterActive
                || this.sentDateFilterComponent?.isFilterActive
                || this.changedOnFilterComponent?.isFilterActive
                || this.referenceNumberFilterComponent?.isFilterActive
                || this.modelTypeFilterComponent?.isFilterActive
                || this.modelCodeFilterComponent?.isFilterActive
                || this.claimCodeFilterComponent?.isFilterActive;
    }

    /**
     * Opens quick vew for a selected Claim
     * @param listItem ClaimListItemModel of the selected claim
     */
    accordionChangeState(listItem: ClaimListItemModel): void {
        if (!listItem.isOpen && !listItem.isTouched) {
            listItem.isOpen = true;
            this.subscriptions.add(
                    this.claimsApi.getClaimDetail(listItem.claim.number).subscribe((res) => {
                                listItem.positions = res.claimPositions;
                                listItem.isTouched = true;
                                listItem.isOpen = true;
                                listItem.contextMenuOptions = this.mapAvailableActions(res.availableActions);
                            },
                            error => {
                                listItem.isTouched = false;
                                listItem.isOpen = false;
                            }));
        } else {
            listItem.isTouched = true;
            listItem.isOpen = !listItem.isOpen;
        }
    }

    /**
     * Determines if a Claim has a quick view
     * @param claimSummary Claim summary model
     */
    showQuickView = (claimSummary: ClaimSummaryModel): boolean => claimSummary.numberOfPositions > 0;

    /**
     * Determines which criteria the SPO list should be ordered
     * @param columnSelected The ordering criteria
     * @param arrowUp If the corresponding arrow orientation is up
     */
    sortingBy(columnSelected: ClaimListFilterColumnEnum, arrowUp: boolean): void {
        if (this.claimSearchCriteriaService.sortedBy === columnSelected && !arrowUp) {
            this.claimSearchCriteriaService.sortDirection = SortDirection.ASC;
        } else {
            this.claimSearchCriteriaService.sortDirection = SortDirection.DESC;
        }
        this.claimSearchCriteriaService.sortedBy = columnSelected;
        this.getClaims(true);
    }

    /**
     * Toggles ordering arrows
     */
    arrowToggle(): void {
        this.partnerArrowUp = (this.claimSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.claimSearchCriteriaService.sortedBy === ClaimListFilterColumnEnum.PARTNER);
        this.supplyingPackingPlantArrowUp = (this.claimSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.claimSearchCriteriaService.sortedBy === ClaimListFilterColumnEnum.SUPPLYING_PACKING_PLANT);
        this.changedOnArrowUp = (this.claimSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.claimSearchCriteriaService.sortedBy === ClaimListFilterColumnEnum.LAST_CHANGE_DATETIME);
        this.lotNumberArrowUp = (this.claimSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.claimSearchCriteriaService.sortedBy === ClaimListFilterColumnEnum.LOT_NUMBER);
        this.numberArrowUp = (this.claimSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.claimSearchCriteriaService.sortedBy === ClaimListFilterColumnEnum.NUMBER);
        this.materialArrowUp = (this.claimSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.claimSearchCriteriaService.sortedBy === ClaimListFilterColumnEnum.MATERIAL);
        this.statusArrowUp = (this.claimSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.claimSearchCriteriaService.sortedBy === ClaimListFilterColumnEnum.STATUS);
        this.sentDateArrowUp = (this.claimSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.claimSearchCriteriaService.sortedBy === ClaimListFilterColumnEnum.SENT_DATE);
        this.referenceNumberArrowUp = (this.claimSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.claimSearchCriteriaService.sortedBy === ClaimListFilterColumnEnum.REFERENCE_NUMBER);
        this.modelTypeArrowUp = (this.claimSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.claimSearchCriteriaService.sortedBy === ClaimListFilterColumnEnum.MODEL_TYPE);
        this.modelCodeArrowUp = (this.claimSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.claimSearchCriteriaService.sortedBy === ClaimListFilterColumnEnum.MODEL_CODE);
        this.claimCodeArrowUp = (this.claimSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.claimSearchCriteriaService.sortedBy === ClaimListFilterColumnEnum.CLAIM_CODE);
    }

    getClaimMaterialPositionTableColspan(): number {
        return this.hasPartnerColumn() ? 13 : 12;
    }

    /**
     * Emits the Claim report criteria
     * @param query The criteria for the report
     * **/
    emitReportCriteria(query: ClaimQueryModel): void {
        this.reportCriteria.emit(query);
    }
}
